/**
 * The reader's own Confirm, and the reminders Mitra keeps for them: the
 * engine's proposal and reminder routes (spec `2026-10-13-alerts-watch-
 * reminders-design`, "confirm in chat").
 *
 * Rule 4 read from this side: the engine proposes, the reader confirms, the
 * platform commits. Nothing here writes on its own initiative. A commit is
 * sent only for a proposal the reader confirmed, and only once their Undo
 * window has closed (`useProposalActions`); a reminder is only ever deleted.
 *
 * Every call NEVER REJECTS, for `shelf.ts`'s reason: a thrown fetch inside a
 * click handler is an unhandled rejection and a control that silently does
 * nothing. Every figure and date a response carries is a string the server
 * wrote (`*_text`, ISO dates); nothing here parses one into a number.
 */
import { engineFetch } from '@/features/mitra/api/askClient'

/** The proposal kinds this flow confirms. A portfolio change keeps its own. */
export const CONFIRM_KINDS = ['alert', 'watchlist', 'reminder', 'dashboard_change'] as const
export type ConfirmKind = (typeof CONFIRM_KINDS)[number]

export function isConfirmKind(value: unknown): value is ConfirmKind {
  return typeof value === 'string' && (CONFIRM_KINDS as readonly string[]).includes(value)
}

export type ProposalStatus = 'pending' | 'cancelled' | 'committed' | 'failed'

export interface ProposalRead {
  proposal_id: string
  kind: string
  status: ProposalStatus
  summary_text: string
  result?: unknown
  /** A failed proposal's code and sentence, where the engine sent them. */
  code?: string | null
  message?: string | null
  /**
   * A dashboard change as it would look now (`preview`): the widgets it adds
   * or edits, as the engine draws them, the dashboard it is for, and the
   * whole-dashboard action where it is one. Read for "Edit before saving".
   */
  preview?: {
    widgets: Record<string, unknown>[]
    dashboardId: string | null
    action: string | null
  }
}

/** What a commit came to, read off its status code. */
export type CommitOutcome =
  | { outcome: 'committed'; result: unknown }
  /** 409 with no code: somebody (this reader, in another tab) already claimed it. */
  | { outcome: 'claimed'; status: ProposalStatus | null }
  /**
   * Refused before anything was claimed, or the claim released: the proposal
   * is still `pending` (`UNAVAILABLE`, `UNKNOWN_GROUP`, `SIGN_IN_REQUIRED`,
   * `NOT_SENT`).
   */
  | { outcome: 'pending'; code: string | null; message: string | null }
  /**
   * The proposal is now `failed`, which is terminal (`UNCONFIRMED`,
   * `REMINDER_CAP`, the platform's own codes), or the request never got an
   * answer at all (`sent: false`, safe to send again: a commit is claimed
   * once, so a repeat of one that landed answers 409).
   */
  | {
      outcome: 'failed'
      code: string | null
      message: string | null
      sent: boolean
      /** `NAME_TAKEN` (a dashboard change): the dashboard that has the name, to open. */
      existing?: string | null | undefined
    }

const STATUSES: readonly string[] = ['pending', 'cancelled', 'committed', 'failed']

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

async function json(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown
  } catch {
    return null
  }
}

/** The engine's ids are opaque, but never a path: one segment of safe characters. */
const ID = /^[A-Za-z0-9_-]{1,80}$/

const statusOf = (value: unknown): ProposalStatus | null =>
  typeof value === 'string' && STATUSES.includes(value) ? (value as ProposalStatus) : null

const textOf = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

/** `GET /v1/proposals/{id}`: where a proposal stands, or null where it cannot be read. */
export async function fetchProposal(id: string): Promise<ProposalRead | null> {
  if (!ID.test(id)) return null
  try {
    const response = await engineFetch(`/v1/proposals/${encodeURIComponent(id)}`)
    if (!response.ok) return null
    const body = await json(response)
    if (!isObject(body)) return null
    const status = statusOf(body.status)
    if (status === null) return null
    return {
      proposal_id: typeof body.proposal_id === 'string' ? body.proposal_id : id,
      kind: typeof body.kind === 'string' ? body.kind : '',
      status,
      summary_text: typeof body.summary_text === 'string' ? body.summary_text : '',
      ...('result' in body ? { result: body.result } : {}),
      code: textOf(body.code),
      message: textOf(body.message),
      ...(isObject(body.preview)
        ? {
            preview: {
              widgets: Array.isArray(body.preview.widgets)
                ? body.preview.widgets.filter(isObject)
                : [],
              dashboardId:
                typeof body.preview.dashboard_id === 'string' &&
                /^d_[0-9a-f]{10}$/.test(body.preview.dashboard_id)
                  ? body.preview.dashboard_id
                  : null,
              // The engine sends the action as `{op, name, ...}`; an older one as its op alone.
              action:
                textOf(body.preview.action) ??
                (isObject(body.preview.action) ? textOf(body.preview.action.op) : null),
            },
          }
        : {}),
    }
  } catch {
    return null
  }
}

/**
 * `POST /v1/proposals/{id}/commit`, with the watchlist group the reader
 * picked where there was a choice (the engine's list ids are integers).
 *
 * Read by the BODY's status first, as the final contract states it: a
 * `pending` body was refused with nothing claimed (503 `UNAVAILABLE` or
 * `NOT_SENT`, 403 `SIGN_IN_REQUIRED`, 422 `UNKNOWN_GROUP`); a `failed` body
 * is terminal (502 `UNCONFIRMED`, 409 `REMINDER_CAP`, the platform's own
 * 4xx/5xx codes); a 409 that names no code was claimed already.
 */
export async function commitProposal(id: string, groupId?: string | null): Promise<CommitOutcome> {
  if (!ID.test(id)) return { outcome: 'failed', code: null, message: null, sent: false }
  try {
    // The engine's list ids are integers; nothing else is a list to send.
    const group = groupId && /^-?\d+$/.test(groupId.trim()) ? Number(groupId) : null
    const response = await engineFetch(`/v1/proposals/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
      json: group !== null ? { group_id: group } : {},
    })
    const body = await json(response)
    const fields = isObject(body) ? body : {}
    const code = textOf(fields.code)
    const message = textOf(fields.message)
    const status = statusOf(fields.status)
    if (response.ok && status !== 'failed' && status !== 'pending') {
      return { outcome: 'committed', result: fields.result ?? null }
    }
    if (status === 'pending' && code !== null) return { outcome: 'pending', code, message }
    if (response.status === 409 && code === null) return { outcome: 'claimed', status }
    if (status === 'failed' || code !== null) {
      const existing = textOf(fields.existing_id)
      return {
        outcome: 'failed',
        code,
        message,
        sent: true,
        ...(existing && /^d_[0-9a-f]{10}$/.test(existing) ? { existing } : {}),
      }
    }
    // No body the contract names: the answer was lost, so nothing says it landed.
    return { outcome: 'failed', code: null, message: null, sent: false }
  } catch {
    return { outcome: 'failed', code: null, message: null, sent: false }
  }
}

/** `GET /v1/portfolio/proposals/{id}`: where a portfolio change stands. */
export interface PortfolioProposalRead {
  /** The engine's status word: `open`, `confirmed`, `rejected`, ... */
  status: string
  committable: boolean
  block_reason: string | null
  /** A confirmed change's place to open (`{destination, symbol}`), where the engine names one. */
  link: unknown
}

export async function readPortfolioProposal(id: string): Promise<PortfolioProposalRead | null> {
  if (!ID.test(id)) return null
  try {
    const response = await engineFetch(`/v1/portfolio/proposals/${encodeURIComponent(id)}`)
    if (!response.ok) return null
    const body = await json(response)
    if (!isObject(body) || typeof body.committable !== 'boolean') return null
    return {
      status: typeof body.status === 'string' ? body.status : '',
      committable: body.committable,
      block_reason: textOf(body.block_reason),
      link: body.link ?? null,
    }
  } catch {
    return null
  }
}

export type PortfolioCommit =
  | { ok: true; result: unknown }
  /** Refused or unanswered; `message` is the engine's sentence (`detail`) where it sent one. */
  | { ok: false; message: string | null; status: number | null }

/** `POST /v1/portfolio/proposals/{id}/commit`: the reader's one Confirm; no body is sent. */
export async function commitPortfolioProposal(id: string): Promise<PortfolioCommit> {
  if (!ID.test(id)) return { ok: false, message: null, status: null }
  try {
    const response = await engineFetch(`/v1/portfolio/proposals/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
    })
    const body = await json(response)
    const fields = isObject(body) ? body : {}
    if (response.ok && fields.status === 'committed') {
      return { ok: true, result: fields.result ?? null }
    }
    return {
      ok: false,
      message: typeof fields.detail === 'string' ? textOf(fields.detail) : null,
      status: response.status,
    }
  } catch {
    return { ok: false, message: null, status: null }
  }
}

/** What this deployment can do, as `GET /v1/capabilities` and each ack state it. */
export interface Capabilities {
  /** Alerts, watches, a portfolio change and an import's Confirm can land on MoneyMitra. */
  platform_writes: boolean
  /**
   * `POST /v1/proposals` is served, so Remind me and Watch sign a proposal
   * with no model round. True whatever `platform_writes` says: a reminder
   * never needs MoneyMitra, and a watch is gated by `platform_writes` alone.
   */
  quick_proposals: boolean
  /**
   * The engine draws Stages (wire contract §14.1). Present, and true, only
   * where the engine said so: absent reads as false, which is the reading of
   * every engine before the field.
   */
  stage?: true
  /** Playable cards, stories and the pen (wire §15.1); absent reads as false. */
  play?: true
  story?: true
  pen?: true
  /** The engine lays out the general answer page (wire §16); absent reads as false. */
  answer_page?: true
  /**
   * This reader may open an answer's Details, the turn's trace: staff only.
   * Absent reads as false, which is every member's reading.
   */
  trace?: true
  /**
   * `AskRequest.page` takes any of the app's pages, not only a company's
   * (engine, 2026-10-30). Absent reads as false: an engine before it refuses
   * a page that is not a company's.
   */
  page_context?: true
}

const NONE: Capabilities = { platform_writes: false, quick_proposals: false }

/**
 * `GET /v1/capabilities` (wire contract §8). Everything false on any
 * failure, a 404 included: an affordance that cannot land is hidden or drawn
 * down until the engine says it can.
 */
export async function fetchCapabilities(): Promise<Capabilities> {
  try {
    const response = await engineFetch('/v1/capabilities')
    if (!response.ok) return NONE
    const body = await json(response)
    if (!isObject(body)) return NONE
    return {
      platform_writes: body.platform_writes === true,
      quick_proposals: body.quick_proposals === true,
      ...(body.stage === true ? { stage: true as const } : {}),
      ...(body.play === true ? { play: true as const } : {}),
      ...(body.story === true ? { story: true as const } : {}),
      ...(body.pen === true ? { pen: true as const } : {}),
      ...(body.answer_page === true ? { answer_page: true as const } : {}),
      ...(body.trace === true ? { trace: true as const } : {}),
      ...(body.page_context === true ? { page_context: true as const } : {}),
    }
  } catch {
    return NONE
  }
}

/** `platform_writes` alone, for a caller that reads nothing else. */
export async function fetchPlatformWrites(): Promise<boolean> {
  return (await fetchCapabilities()).platform_writes
}

// ------------------------------------------------- model-free proposals

/**
 * What "Remind me" and Watch ask for, model-free (2026-10-13): the engine
 * signs a proposal and hands back the ActionCard element it would have drawn
 * on a turn, so the tap no longer sends a templated question for a model to
 * read back. Nothing is written by this call: it is a proposal, and the
 * reader's Confirm is still the only way to a commit.
 */
export type ProposalRequest = (
  { kind: 'reminder'; event_ref: string } | { kind: 'watchlist'; symbol: string }
) & {
  /** The card's month words and digits; absent is English. */
  register?: 'en' | 'ne-latn' | 'ne'
}

export type ProposeOutcome =
  /** Signed: draw `element` (an ActionCard) under the card that was tapped. */
  | { outcome: 'proposed'; proposal_id: string; element: Record<string, unknown> }
  /** The route is not on this engine yet: keep the button down, ask nothing. */
  | { outcome: 'absent' }
  /** Refused, or no answer: the engine's sentence where it sent one. */
  | { outcome: 'refused'; message: string | null }

/**
 * FastAPI's own 404 for a path no route matches. A route that exists and
 * answers 404 (an event that is no longer on record) says something else, so
 * only this reads as "not built yet".
 */
function routeMissing(status: number, body: unknown): boolean {
  if (status === 405) return true
  if (status !== 404) return false
  return !isObject(body) || body.detail === 'Not Found' || body.detail === undefined
}

/** `POST /v1/proposals`: a proposal the engine signs without a model round. */
export async function requestProposal(request: ProposalRequest): Promise<ProposeOutcome> {
  try {
    const response = await engineFetch('/v1/proposals', { method: 'POST', json: request })
    const body = await json(response)
    if (routeMissing(response.status, body)) return { outcome: 'absent' }
    const fields = isObject(body) ? body : {}
    const element = fields.element
    const id = fields.proposal_id
    if (
      response.ok &&
      typeof id === 'string' &&
      ID.test(id) &&
      isObject(element) &&
      element.type === 'ActionCard' &&
      isObject(element.props)
    ) {
      return { outcome: 'proposed', proposal_id: id, element }
    }
    const detail = fields.detail
    return {
      outcome: 'refused',
      message: textOf(fields.message) ?? (typeof detail === 'string' ? textOf(detail) : null),
    }
  } catch {
    return { outcome: 'refused', message: null }
  }
}

/** `POST /v1/proposals/{id}/cancel`: 204 from the engine; true where it answered so. */
export async function cancelProposal(id: string): Promise<boolean> {
  if (!ID.test(id)) return false
  try {
    const response = await engineFetch(`/v1/proposals/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * The words a committed proposal's `result` carries, where it carries any.
 * The contract names `result` and not its shape, so a string is read as is
 * and an object for its `*_text` sentence; nothing else is drawn.
 */
export function resultText(result: unknown): string | null {
  if (typeof result === 'string') return textOf(result)
  if (!isObject(result)) return null
  // `result_text` first: every result the final contract names opens with it.
  for (const key of ['result_text', 'summary_text', 'message_text', 'text']) {
    const text = textOf(result[key])
    if (text) return text
  }
  return null
}

// --------------------------------------------------------------- reminders

export type ReminderState = 'on_time' | 'moved' | 'cancelled'

export interface Reminder {
  reminder_id: string
  kind: string
  symbol: string | null
  title_text: string
  /** ISO date, the event's own. */
  event_date: string | null
  /** ISO date, the day the reminder is for. */
  remind_on: string | null
  state: ReminderState
  /** The engine's sentence about a moved or cancelled event; null otherwise. */
  note_text: string | null
}

const REMINDER_STATES: readonly string[] = ['on_time', 'moved', 'cancelled']

function reminderOf(value: unknown): Reminder | null {
  if (!isObject(value)) return null
  const id = value.reminder_id
  const title = textOf(value.title_text)
  if (typeof id !== 'string' || !ID.test(id) || !title) return null
  return {
    reminder_id: id,
    kind: typeof value.kind === 'string' ? value.kind : '',
    symbol: textOf(value.symbol),
    title_text: title,
    event_date: textOf(value.event_date),
    remind_on: textOf(value.remind_on),
    state:
      typeof value.state === 'string' && REMINDER_STATES.includes(value.state)
        ? (value.state as ReminderState)
        : 'on_time',
    note_text: textOf(value.note_text),
  }
}

/** `GET /v1/reminders?due=...`: the reader's reminders, or null on any failure. */
export async function listReminders(due: 'today' | 'all'): Promise<Reminder[] | null> {
  try {
    const response = await engineFetch(`/v1/reminders?due=${due}`)
    if (!response.ok) return null
    const body = await json(response)
    if (!isObject(body) || !Array.isArray(body.reminders)) return null
    return body.reminders.map(reminderOf).filter((r): r is Reminder => r !== null)
  } catch {
    return null
  }
}

/** `DELETE /v1/reminders/{id}`: true where the engine answered 2xx. */
export async function deleteReminder(id: string): Promise<boolean> {
  if (!ID.test(id)) return false
  try {
    const response = await engineFetch(`/v1/reminders/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
    return response.ok
  } catch {
    return false
  }
}
